import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';

interface TaxonomyTerm {
  id: string;
  taxonomy: string;
  value: string;
  label: string;
  sort_order: number;
  is_active: boolean;
}

const SECTIONS = [
  { taxonomy: 'industry', title: 'Industry' },
  { taxonomy: 'role_archetype', title: 'Role archetype' },
  { taxonomy: 'seniority_level', title: 'Seniority level' },
] as const;

/**
 * Admin management of the guided-selection taxonomy — the DB-backed
 * replacement for what used to be hardcoded PHP enums
 * (Industry/RoleArchetype/SeniorityLevel). Candidates pick from these
 * same value/label pairs in the onboarding guided-selection step.
 */
export function TaxonomyPage() {
  const [terms, setTerms] = useState<TaxonomyTerm[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  function load() {
    api<{ data: TaxonomyTerm[] }>('/admin/taxonomy-terms')
      .then((r) => setTerms(r.data))
      .catch(() => setTerms([]));
  }
  useEffect(load, []);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-black tracking-tight">Job role taxonomy</h1>
        <p className="text-sm text-muted-foreground">
          Industry, role archetype, and seniority options for the candidate guided
          onboarding-selection step. A term's <code>value</code> can't change after creation — it's
          what's already stored on candidate profiles.
        </p>
      </header>

      {error !== null && (
        <p
          role="alert"
          className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </p>
      )}

      {SECTIONS.map((section) => (
        <TaxonomySection
          key={section.taxonomy}
          taxonomy={section.taxonomy}
          title={section.title}
          terms={terms?.filter((t) => t.taxonomy === section.taxonomy) ?? null}
          onError={setError}
          onReload={load}
        />
      ))}
    </div>
  );
}

function TaxonomySection({
  taxonomy,
  title,
  terms,
  onError,
  onReload,
}: {
  taxonomy: string;
  title: string;
  terms: TaxonomyTerm[] | null;
  onError: (message: string) => void;
  onReload: () => void;
}) {
  const [draft, setDraft] = useState({ value: '', label: '' });

  async function create() {
    onError('');
    try {
      await api('/admin/taxonomy-terms', {
        method: 'POST',
        json: { taxonomy, value: draft.value, label: draft.label },
      });
      setDraft({ value: '', label: '' });
      onReload();
    } catch {
      onError(
        `Couldn't add the ${title.toLowerCase()} term — check the value is unique and snake_case.`,
      );
    }
  }

  async function remove(term: TaxonomyTerm) {
    if (
      !window.confirm(
        `Delete "${term.label}"? Candidates who selected it will fall back to "not declared".`,
      )
    )
      return;
    try {
      await api(`/admin/taxonomy-terms/${term.id}`, { method: 'DELETE' });
      onReload();
    } catch {
      onError('Delete failed.');
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {title}{' '}
          <span className="text-sm font-normal text-muted-foreground">({terms?.length ?? 0})</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {terms === null && <div className="h-16 animate-pulse rounded-2xl bg-muted" />}
        {terms !== null && terms.length > 0 && (
          <ul className="space-y-2">
            {terms
              .slice()
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((term) => (
                <TermRow
                  key={term.id}
                  term={term}
                  onError={onError}
                  onRemove={remove}
                  onSaved={onReload}
                />
              ))}
          </ul>
        )}

        <form
          className="flex flex-wrap items-end gap-3 border-t border-border pt-4"
          onSubmit={(e) => {
            e.preventDefault();
            void create();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor={`${taxonomy}-value`}>New value (snake_case)</Label>
            <Input
              id={`${taxonomy}-value`}
              value={draft.value}
              onChange={(e) => setDraft({ ...draft, value: e.target.value })}
              placeholder="e.g. aerospace"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${taxonomy}-label`}>Label</Label>
            <Input
              id={`${taxonomy}-label`}
              value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              placeholder="e.g. Aerospace"
            />
          </div>
          <Button type="submit" size="sm" disabled={draft.value === '' || draft.label === ''}>
            <Plus className="mr-2 h-4 w-4" /> Add
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function TermRow({
  term,
  onError,
  onRemove,
  onSaved,
}: {
  term: TaxonomyTerm;
  onError: (message: string) => void;
  onRemove: (term: TaxonomyTerm) => void;
  onSaved: () => void;
}) {
  const [label, setLabel] = useState(term.label);
  const [sortOrder, setSortOrder] = useState(String(term.sort_order));
  const dirty = label !== term.label || sortOrder !== String(term.sort_order);

  async function save() {
    try {
      await api(`/admin/taxonomy-terms/${term.id}`, {
        method: 'PATCH',
        json: { label, sort_order: Number(sortOrder) || 0 },
      });
      onSaved();
    } catch {
      onError('Save failed.');
    }
  }

  async function toggleActive() {
    try {
      await api(`/admin/taxonomy-terms/${term.id}`, {
        method: 'PATCH',
        json: { is_active: !term.is_active },
      });
      onSaved();
    } catch {
      onError('Update failed.');
    }
  }

  return (
    <li className="flex flex-wrap items-center gap-3 rounded-2xl border border-border p-3 text-sm">
      <Badge variant="secondary" className="font-mono">
        {term.value}
      </Badge>
      <Input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        className="h-9 max-w-[220px]"
        aria-label={`Label for ${term.value}`}
      />
      <Input
        type="number"
        min={0}
        value={sortOrder}
        onChange={(e) => setSortOrder(e.target.value)}
        className="h-9 w-20"
        aria-label={`Sort order for ${term.value}`}
      />
      {dirty && (
        <Button size="sm" onClick={() => void save()}>
          Save
        </Button>
      )}
      <Button size="sm" variant="outline" onClick={() => void toggleActive()}>
        {term.is_active ? 'Active' : 'Inactive'}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="ml-auto text-destructive"
        onClick={() => onRemove(term)}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </li>
  );
}
